import { useMediaFilePreview } from '@lib/hooks/preview/preview';
import { useEffect, useState } from 'react';

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

export function useMediaData<T>(
  dataFile: string,
  libraryId?: string,
  mediaId?: string,
  cacheKey?: string,
) {
  const [data, setData] = useState<T | null | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [retryCount, setRetryCount] = useState(0);
  const { url, token } = useMediaFilePreview(libraryId, mediaId, `/data/${dataFile}`);

  const getData = async (
    dataUrl: string,
    authToken: string | null,
    controller: AbortController,
  ): Promise<T> => {
    const fetchOptions: RequestInit = {
      signal: controller.signal,
      cache: 'default',
    };

    if (authToken) {
      fetchOptions.headers = {
        Authorization: `Bearer ${authToken}`,
      };
    }

    const response = await fetch(dataUrl, fetchOptions);

    if (response.status === 404) {
      const err = new Error('Not found');
      err.name = 'NotFoundError';
      throw err;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch ${dataFile} for ${libraryId}/${mediaId}`);
    }
    const data = (await response.json()) as T;

    return data;
  };

  useEffect(() => {
    if (!url) {
      setData(undefined);
      setError(undefined);
      setRetryCount(0);
      return;
    }

    const controller = new AbortController();
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    // Cache busting: Add cache key (typically media.status) as query parameter
    // When media status changes (e.g., from 'processing' to 'completed'),
    // this triggers a refetch via the useEffect dependency on cacheKey,
    // ensuring we get the latest transcript/visual data when processing completes
    let fetchUrl = url;
    if (cacheKey) {
      const urlObj = new URL(url);
      urlObj.searchParams.set('v', cacheKey);
      fetchUrl = urlObj.toString();
    }

    const attemptFetch = async (attempt: number) => {
      try {
        const result = await getData(fetchUrl, token, controller);
        setData(result);
        setError(undefined);
        setRetryCount(0);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        if (err instanceof Error && err.name === 'NotFoundError') {
          setData(null);
          setError(undefined);
          setRetryCount(0);
          return;
        }

        if (attempt < MAX_RETRIES) {
          console.warn(`Failed to fetch ${dataFile}, retrying (${attempt + 1}/${MAX_RETRIES})...`);
          setRetryCount(attempt + 1);
          // Exponential backoff: 1s, 2s, 4s
          const retryDelay = RETRY_DELAY_MS * Math.pow(2, attempt);
          retryTimer = setTimeout(() => attemptFetch(attempt + 1), retryDelay);
        } else {
          console.error(`Failed to fetch ${dataFile} after ${MAX_RETRIES} attempts:`, err);
          setError(err instanceof Error ? err : new Error(String(err)));
          setRetryCount(0);
        }
      }
    };

    attemptFetch(0);

    return () => {
      controller.abort();
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
      }
      setRetryCount(0);
    };
  }, [url, token, cacheKey, dataFile]);

  return { data, error, isRetrying: retryCount > 0 };
}
